"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { motion, AnimatePresence } from "motion/react";
import type { PropertyFloorPlan as FloorPlanType } from "@/types/database";

// Default floor plans for fallback
const defaultFloorPlans = [
    {
        id: 1,
        property_id: 0,
        label: "4 BR",
        title: "4 Bedroom Villa (Type A - Ground Floor)",
        specs: [
            "1 GUEST BEDROOM + 1 LIVING ROOM + 1 DINING AREA + 1 POWDER ROOM + 1 STORE ROOM + MAID ROOM",
            "SALEABLE AREA: 4,905.33 SQ.FT."
        ],
        area_sqft: 4905.33,
        image_url: "/images/floor-plan.png",
        display_order: 1
    },
    {
        id: 2,
        property_id: 0,
        label: "5 BR",
        title: "5 Bedroom Villa (Type B - Ground Floor)",
        specs: [
            "1 GUEST BEDROOM + 1 LIVING ROOM + 1 DINING AREA + 1 POWDER ROOM + 1 STORE ROOM + MAID ROOM",
            "SALEABLE AREA: 5,905.33 SQ.FT."
        ],
        area_sqft: 5905.33,
        image_url: "/images/floor-plan.png",
        display_order: 2
    },
    {
        id: 3,
        property_id: 0,
        label: "6 BR",
        title: "6 Bedroom Villa (Type C - Ground Floor)",
        specs: [
            "2 GUEST BEDROOMS + 1 LIVING ROOM + 1 DINING AREA + 1 POWDER ROOM + 1 STORE ROOM + MAID ROOM",
            "SALEABLE AREA: 7,500.00 SQ.FT."
        ],
        area_sqft: 7500.00,
        image_url: "/images/floor-plan.png",
        display_order: 3
    }
];

interface PropertyFloorPlanProps {
    floorPlans?: FloorPlanType[];
}

export function PropertyFloorPlan({ floorPlans }: PropertyFloorPlanProps) {
    // Use provided floor plans or defaults
    const plans = floorPlans && floorPlans.length > 0 ? floorPlans : defaultFloorPlans;

    const [activeTab, setActiveTab] = useState(String(plans[0]?.id || "1"));
    const activePlan = plans.find(p => String(p.id) === activeTab) || plans[0];

    return (
        <section className="py-24 px-6 md:px-12 max-w-[1400px] mx-auto">
            <div className="text-center mb-16 space-y-8 flex flex-col items-center">
                <div className="space-y-4">
                    <span className="text-xs font-bold tracking-[0.3em] text-secondary uppercase block">
                        Layouts
                    </span>
                    <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[0.9] text-primary">
                        Floor <span className="italic text-primary/50">Plans</span>
                    </h2>
                </div>

                <div className="inline-flex items-center justify-center border-b border-muted w-full md:w-auto">
                    {plans.map((plan) => (
                        <button
                            type="button"
                            key={plan.id}
                            onClick={() => setActiveTab(String(plan.id))}
                            className={cn(
                                "px-8 py-4 text-xs font-bold uppercase tracking-widest transition-all relative",
                                activeTab === String(plan.id) ? "text-primary" : "text-muted-foreground hover:text-primary/70"
                            )}
                        >
                            {plan.label}
                            {activeTab === String(plan.id) && (
                                <motion.div
                                    layoutId="activeTab"
                                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                                />
                            )}
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 items-start">
                <div className="flex-1 space-y-8 pt-8">
                    <motion.div
                        key={activePlan.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4 }}
                    >
                        <h3 className="font-serif text-2xl md:text-3xl text-primary/80 mb-8">{activePlan.title}</h3>

                        <div className="space-y-6">
                            {activePlan.specs[0] && (
                                <div>
                                    <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Unit :</h4>
                                    <p className="text-sm font-medium leading-relaxed max-w-md uppercase">
                                        {activePlan.specs[0]}
                                    </p>
                                </div>
                            )}

                            {(activePlan.specs[1] || activePlan.area_sqft) && (
                                <div>
                                    <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Saleable Area :</h4>
                                    <p className="text-sm font-medium uppercase">
                                        {activePlan.specs[1] || `SALEABLE AREA: ${activePlan.area_sqft?.toLocaleString()} SQ.FT.`}
                                    </p>
                                </div>
                            )}
                        </div>

                        <Button variant="outline" className="mt-12 rounded-xl px-8 h-10 uppercase tracking-widest text-xs font-bold border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300">
                            Download Floor Plan
                        </Button>
                    </motion.div>
                </div>

                <div className="flex-1 w-full bg-muted/10 p-8 rounded-xl">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activePlan.id}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.4 }}
                            className="aspect-[4/3] relative rounded-lg overflow-hidden"
                        >
                            {/* Placeholder for floor plan image - using object-contain to mimic technical drawing feel */}
                            <Image
                                src={activePlan.image_url || "/images/floor-plan.png"}
                                alt={activePlan.title}
                                fill
                                className="object-contain bg-white"
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.src = "/images/floor-plan-v2.png";
                                }}
                            />
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </section>
    );
}
